import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Building2, Plus, ArrowRight, ArrowLeft, Loader2, AlertTriangle, ChevronRight, Trash2, Eye, Brain, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { cn } from "@/lib/utils";
import { CompanyProfileBuilderDialog } from "@/components/ai-knowledge/CompanyProfileBuilderDialog";
import { warmProfileBuilder } from "@/lib/profileBuilderApi";
import { CompanyKnowledgeDetailsDialog } from "@/components/ai-knowledge/CompanyKnowledgeDetailsDialog";
import type { CompanyKnowledgeProfile, BusinessProfile } from "@/components/ai-knowledge/types";
import type { Json } from "@/integrations/supabase/types";
import { profileCompleteness } from "@/lib/profileCompleteness";

// Extended type to include the guest token field
interface ExtendedCompanyKnowledgeProfile extends CompanyKnowledgeProfile {
  created_by_guest_token?: string | null;
}

interface AiSelectBusinessContextStepProps {
  workspaceId: string;
  selectedProfileId: string | null;
  onSelect: (profileId: string) => void;
  onNext: () => void;
  onBack: () => void;
  guestToken?: string;
}

export function AiSelectBusinessContextStep({
  workspaceId,
  selectedProfileId,
  onSelect,
  onNext,
  onBack,
  guestToken,
}: AiSelectBusinessContextStepProps) {
  const [editProfileId, setEditProfileId] = useState<string | null>(null);
  const [viewDetailsProfileId, setViewDetailsProfileId] = useState<string | null>(null);
  const [deleteProfileId, setDeleteProfileId] = useState<string | null>(null);
  // Profile row created for the builder; hidden until information is read or saved.
  const [draftProfileId, setDraftProfileId] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const { data: profiles, isLoading } = useQuery({
    queryKey: ["company-knowledge-profiles", workspaceId, guestToken],
    queryFn: async () => {
      if (guestToken) {
        // Use RPC for guest access - now returns created_by_guest_token
        const { data, error } = await supabase.rpc('get_guest_company_profiles', {
          token_uuid: guestToken
        });
        if (error) throw error;
        return data as ExtendedCompanyKnowledgeProfile[];
      } else {
        // Normal authenticated access
        const { data, error } = await supabase
          .from("company_knowledge_profiles")
          .select("*")
          .eq("workspace_id", workspaceId)
          .order("updated_at", { ascending: false });

        if (error) throw error;
        return data as ExtendedCompanyKnowledgeProfile[];
      }
    },
    enabled: !!workspaceId,
  });

  useEffect(() => { warmProfileBuilder(); }, []);

  const createMutation = useMutation({
    mutationFn: async (companyName: string) => {
      if (guestToken) {
        // Use RPC for guest creation
        const { data, error } = await supabase.rpc('guest_create_company_profile', {
          token_uuid: guestToken,
          company_name_input: companyName
        });
        if (error) throw error;
        if (!data || data.length === 0) throw new Error("Failed to create profile");
        return data[0];
      } else {
        // Use edge function for authenticated users
        const { data, error } = await supabase.functions.invoke("ai-knowledge-assistant", {
          body: {
            action: "create",
            type: "company",
            workspaceId,
            companyName,
          },
        });

        if (error) throw error;
        if (data.error) throw new Error(data.error);
        return data;
      }
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["company-knowledge-profiles", workspaceId, guestToken] });
      setDraftProfileId(data.id);
      // Open edit dialog for the new profile
      setEditProfileId(data.id);
    },
    onError: (error: Error) => {
      toast.error("Failed to create profile: " + error.message);
    },
  });

  // Delete mutation for guest users
  const deleteMutation = useMutation({
    mutationFn: async (profileId: string) => {
      if (guestToken) {
        const { data, error } = await supabase.rpc('guest_delete_company_profile', {
          token_uuid: guestToken,
          profile_uuid: profileId
        });
        if (error) throw error;
        return data;
      } else {
        // For authenticated users, delete via direct query
        const { error } = await supabase
          .from("company_knowledge_profiles")
          .delete()
          .eq("id", profileId);
        if (error) throw error;
        return true;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["company-knowledge-profiles", workspaceId, guestToken] });
      // Clear selection if deleted profile was selected
      if (deleteProfileId === selectedProfileId) {
        onSelect("");
      }
      setDeleteProfileId(null);
      toast.success("Profile deleted successfully");
    },
    onError: (error: Error) => {
      toast.error("Failed to delete profile: " + error.message);
    },
  });

  // Closing the builder: drop the draft unless information was read into it.
  const closeBuilder = async () => {
    const id = draftProfileId;
    setEditProfileId(null);
    if (!id) return;
    const key = ["company-knowledge-profiles", workspaceId, guestToken];
    // Check the server copy while the draft stays hidden, so no empty card flashes.
    let facts: unknown = null;
    try {
      const { data: row } = guestToken
        ? { data: (queryClient.getQueryData<any[]>(key) ?? []).find((p) => p.id === id) }
        : await supabase.from("company_knowledge_profiles").select("extracted_facts").eq("id", id).maybeSingle();
      facts = (row as any)?.extracted_facts ?? null;
    } catch { /* treat as empty */ }
    if (guestToken && !facts) {
      await queryClient.invalidateQueries({ queryKey: key });
      facts = ((queryClient.getQueryData<any[]>(key) ?? []).find((p) => p.id === id) as any)?.extracted_facts ?? null;
    }
    if (!facts) {
      queryClient.setQueryData<any[]>(key, (old) => (old ?? []).filter((p) => p.id !== id));
      if (selectedProfileId === id) onSelect("");
      (async () => {
        try {
          if (guestToken) await supabase.rpc("guest_delete_company_profile", { token_uuid: guestToken, profile_uuid: id });
          else await supabase.from("company_knowledge_profiles").delete().eq("id", id);
        } catch { /* stays hidden */ }
        queryClient.invalidateQueries({ queryKey: key });
      })();
    } else {
      await queryClient.invalidateQueries({ queryKey: key });
      onSelect(id);
    }
    setDraftProfileId(null);
  };


  const selectedProfile = profiles?.find((p) => p.id === selectedProfileId);
  useEffect(() => {
    if (profiles && selectedProfileId && selectedProfileId !== draftProfileId && !profiles.some((p) => p.id === selectedProfileId)) onSelect("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profiles, selectedProfileId]);
  const businessProfile = selectedProfile?.business_profile as BusinessProfile | null;

  if (isLoading) {
    return (
      <div className="space-y-6 animate-in fade-in duration-500">
        <div className="space-y-1">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-5 w-96" />
        </div>
        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-4">
              <Skeleton className="h-12 w-12 rounded-lg mb-3" />
              <Skeleton className="h-5 w-32 mb-2" />
              <Skeleton className="h-4 w-24" />
            </Card>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="space-y-1">
          <h2 className="text-2xl font-bold tracking-tight">Select Your Business</h2>
          <p className="text-muted-foreground">
            Which business is this campaign for? AI will use this context to generate your campaign.
          </p>
        </div>
        {(
          <Button
            className="shrink-0 gap-2"
            disabled={createMutation.isPending}
            onClick={() => createMutation.mutate("New company")}
          >
            {createMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
            Add business
          </Button>
        )}
      </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {profiles?.filter((p) => p.id !== draftProfileId).map((profile) => {
            const isSelected = profile.id === selectedProfileId;
            const p2 = profile as any;
            const hasFacts = !!p2.extracted_facts && (p2.facts_status === "confirmed" || p2.facts_status === "review");
            const isBuilder = hasFacts && p2.facts_status === "confirmed";
            const inReview = hasFacts && p2.facts_status === "review";
            const comp = hasFacts ? profileCompleteness(p2.extracted_facts, p2.gap_answers ?? {}) : null;
            const score = comp ? comp.score : profile.knowledge_score;
            const hasLowScore = score < 100;
            // Builder profiles are usable once confirmed; missing items are shown, not blocking.
            const isComplete = isBuilder || (!hasFacts && profile.knowledge_score === 100);
            const bp = profile.business_profile as BusinessProfile | null;
            const isGuestOwned = guestToken && profile.created_by_guest_token === guestToken;
            const canEdit = !guestToken || isGuestOwned;

            return (
              <Card
                key={profile.id}
                className={cn(
                  "transition-all group cursor-pointer hover:shadow-md",
                  isBuilder && score === 100 && "border-sage/40",
                  inReview && "border-warning/50 bg-warning/5",
                  !isComplete && !inReview && "opacity-60",
                  isSelected && isComplete ? "ring-2 ring-primary shadow-md" : "hover:border-primary/50"
                )}
                onClick={() => {
                  if (isComplete) onSelect(profile.id);
                  else if (inReview && canEdit) setEditProfileId(profile.id);
                }}
              >
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-3">
                    <div className={cn("p-2 rounded-lg", isBuilder && score === 100 ? "bg-sage/10" : "bg-primary/10")}>
                      <Building2 className={cn("h-5 w-5", isBuilder && score === 100 ? "text-sage" : "text-primary")} />
                    </div>
                    <div className="flex items-center gap-1.5">
                      {inReview ? (
                        <Badge variant="outline" className="border-warning/50 bg-warning/10 text-warning text-[10px]">Not saved</Badge>
                      ) : isBuilder && score === 100 ? (
                        <Badge className="bg-sage text-sage-foreground hover:bg-sage text-[10px] gap-1"><CheckCircle2 className="h-3 w-3" />Ready</Badge>
                      ) : (
                        <>
                          {hasLowScore && !isBuilder && <AlertTriangle className="h-3.5 w-3.5 text-warning" />}
                          <Brain className={cn("h-4 w-4", score === 100 ? "text-sage" : "text-warning")} />
                          <span className={cn("text-xs font-medium",
                            score === 100 ? "text-sage" : score >= 30 ? "text-warning" : "text-destructive")}>
                            {score}%
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                  <h3 className="font-semibold truncate">{profile.company_name}</h3>
                  {comp && (
                    <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
                      <div className={cn("h-full rounded-full", score === 100 ? "bg-sage" : "bg-warning")} style={{ width: `${score}%` }} />
                    </div>
                  )}
                  {inReview && canEdit && (
                    <Button size="sm" className="mt-3 h-8 w-full" onClick={(e) => { e.stopPropagation(); setEditProfileId(profile.id); }}>
                      Finish & save profile
                    </Button>
                  )}
                  {comp && comp.missing.length > 0 && !inReview && (
                    <button type="button" className="mt-2 inline-flex max-w-full items-center gap-1 rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning hover:bg-warning/20"
                      onClick={(e) => { e.stopPropagation(); if (canEdit) setEditProfileId(profile.id); }}>
                      <span className="truncate">Missing: {comp.missing.map((m) => m.label).join(", ")}</span>
                    </button>
                  )}
                  {bp?.industry && (
                    <p className="text-xs text-muted-foreground mt-1 truncate">
                      {bp.industry}
                    </p>
                  )}
                    <div className="flex items-center justify-between mt-3">
                      <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-xs text-muted-foreground">
                          {profile.updated_at && !isNaN(new Date(profile.updated_at).getTime())
                            ? formatDistanceToNow(new Date(profile.updated_at), { addSuffix: true })
                            : "Just created"}
                        </span>
                        {guestToken && isGuestOwned && (
                          <Badge variant="outline" className="text-[9px] px-1 py-0 h-3.5 text-muted-foreground border-muted-foreground/30">
                            You
                          </Badge>
                        )}
                      </div>
                      <div className="flex gap-1">
                        {/* Only show delete button if not guest OR if this guest created the profile */}
                        {canEdit && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity text-destructive hover:text-destructive hover:bg-destructive/10"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteProfileId(profile.id);
                            }}
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        )}
                        {/* Show Edit for guest-owned or authenticated, View Details for non-guest-owned */}
                        {canEdit ? (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setEditProfileId(profile.id);
                            }}
                          >
                            Edit <ChevronRight className="h-3 w-3 ml-1" />
                          </Button>
                        ) : (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 text-xs opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setViewDetailsProfileId(profile.id);
                            }}
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View
                          </Button>
                        )}
                      </div>
                    </div>
                </CardContent>
              </Card>
            );
          })}

        </div>


      <div className="flex justify-between pt-4">
        <Button variant="outline" onClick={onBack} className="gap-2">
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={onNext}
          disabled={!selectedProfileId}
          className="gap-2"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>

      {/* Edit Dialog (Chat) */}
      {editProfileId && (
        <CompanyProfileBuilderDialog
          profileId={editProfileId}
          isNew={editProfileId === draftProfileId}
          workspaceId={workspaceId}
          open={!!editProfileId}
          onOpenChange={(open) => { if (!open) closeBuilder(); }}
          guestToken={guestToken}
          onSaved={() => {
            if (draftProfileId && editProfileId === draftProfileId) { onSelect(draftProfileId); setDraftProfileId(null); }
            queryClient.invalidateQueries({ queryKey: ["company-knowledge-profiles", workspaceId, guestToken] });
          }}
        />
      )}

      {/* View Details Dialog (read-only) */}
      {viewDetailsProfileId && (
        <CompanyKnowledgeDetailsDialog
          profileId={viewDetailsProfileId}
          open={!!viewDetailsProfileId}
          onOpenChange={(open) => !open && setViewDetailsProfileId(null)}
          guestToken={guestToken}
        />
      )}

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteProfileId} onOpenChange={(open) => !open && setDeleteProfileId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Business Profile</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this business profile? This will also delete all associated campaign strategies. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteProfileId && deleteMutation.mutate(deleteProfileId)}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
